
import { supabase } from '@/integrations/supabase/client';

/**
 * Fetches the Stripe account ID for the current user
 * Uses the consistent pattern of ordering by updated_at desc and limiting to 1
 * This is the same pattern used in the connect-stripe edge function
 */
export async function fetchStripeAccountId(): Promise<string | null> {
  try {
    
    // First check if we have a valid session
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !sessionData.session) {
      console.error('[Stripe Debug] Error fetching auth session:', sessionError || 'No session found');
      console.log('[Stripe Debug] Session check details:', JSON.stringify({
        hasError: !!sessionError,
        hasSession: !!sessionData?.session,
        errorMessage: sessionError?.message
      }));
      return null;
    }
    
    // Use a more direct and explicit query to ensure we get the right profile
    const { data, error } = await supabase
      .from('profiles')
      .select('id, stripe_account_id')
      .eq('id', sessionData.session.user.id)
      .single();
    
    if (error) {
      console.error('[Stripe Debug] Error fetching Stripe account ID:', error);
      return null;
    }
    
    if (!data) {
      console.log('[Stripe Debug] No profile found for current user');
      return null;
    }
    
    const accountId = data?.stripe_account_id || null;
    return accountId;
  } catch (err) {
    console.error('[Stripe Debug] Error in fetchStripeAccountId:', err);
    return null;
  }
}

/**
 * Checks if the current user has a connected Stripe account
 * Returns bool for easy checks in components
 */
export async function hasConnectedStripeAccount(): Promise<boolean> {
  const accountId = await fetchStripeAccountId();
  const isConnected = !!accountId; // Convert to boolean - null, empty string, etc. become false
  console.log('[Stripe Debug] Stripe account connection check:', isConnected ? 'Connected' : 'Not connected');
  return isConnected;
}
